import type { FluxErrorEntityErrorCodes } from '../types.js';

export const AutomationV2ActionErrorCodes = {
  AutomationV2ActionInvalidProperties: {
    code: 'automation-v2-action/invalid-properties',
    explanation: `As propriedades configuradas para esta ação **não são válidas**.

**Causas comuns:**
- Campos obrigatórios da ação não foram preenchidos
- Tipos de dados incorretos (ex: texto onde se esperava número)
- Referências a IDs que não existem mais

**Como diagnosticar:**
Revise a configuração da ação na interface de automação e certifique-se de que todos os campos obrigatórios estão preenchidos corretamente.`,
    localizations: {},
    message:
      'As propriedades da ação de automação são inválidas. Verifique os dados e tente novamente.',
    supportingDocs: [
      {
        name: 'Configuração de Ações',
        path: '/wiki/docs/automations/criando-sua-automacao',
      },
    ],
  },
  AutomationV2ActionNotFound: {
    code: 'automation-v2-action/not-found',
    explanation: `A ação solicitada não foi encontrada na definição da automação.

**Causas comuns:**
- A ação foi **excluída** recentemente
- Erro de sincronização entre a versão editada e a salva

**Como resolver:**
Recarregue a automação. Se o erro persistir, remova e adicione a ação novamente.`,
    localizations: {},
    message: 'A ação da automação não foi encontrada.',
    supportingDocs: [
      {
        name: 'Ações de Automação',
        path: '/wiki/docs/category/ações',
      },
    ],
  },
  AutomationV2ActionNotFoundInVersion: {
    code: 'automation-v2-action/not-found-in-version',
    explanation: `A ação referenciada não existe na **versão atual** da automação.

**Causas comuns:**
- Tentativa de acessar uma ação de uma versão antiga que foi removida na nova versão
- Inconsistência no versionamento da automação

**Como diagnosticar:**
Verifique o histórico de versões da automação.`,
    localizations: {},
    message: 'A ação especificada não foi encontrada nesta versão da automação.',
  },
  AutomationV2ActionTypeNotFound: {
    code: 'automation-v2-action/type-not-found',
    explanation: `O **tipo de ação** especificado é desconhecido pelo sistema.

**Causas comuns:**
- O tipo de ação foi descontinuado
- Erro na digitação do identificador do tipo de ação (payload manual)
- Feature flag desabilitada para este tipo de ação

**Como resolver:**
Selecione um tipo de ação válido disponível no menu de ações.`,
    localizations: {},
    message:
      'O tipo de ação especificado não foi encontrado ou não está disponível. Verifique se o tipo de ação está correto.',
    supportingDocs: [
      {
        name: 'Ações de Automação',
        path: '/wiki/docs/category/ações',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-action'>;

export const AutomationV2AutocompleteErrorCodes = {
  AutomationV2AutocompleteNotFound: {
    code: 'automation-v2-autocomplete/not-found',
    explanation: `O recurso alvo do autocomplete não retornou resultados.

**Causas comuns:**
- A entidade buscada não existe
- Termo de busca muito específico sem correspondência

**Como resolver:**
Tente termos de busca mais genéricos.`,
    localizations: {},
    message: 'A entidade solicitada para completar não foi encontrada.',
  },
  AutomationV2AutocompletePipeIdRequired: {
    code: 'automation-v2-autocomplete/pipe-id-required',
    explanation: `O autocomplete depende de um contexto de **Pipe (Fluxo)** que não foi fornecido.

**Causas comuns:**
- Chamada de API sem o parâmetro \`pipeId\`
- Uso do autocomplete fora do contexto de um fluxo

**Como resolver:**
Forneça o ID do fluxo onde a automação está sendo executada.`,
    localizations: {},
    message: 'Esse tipo de autocomplete requer um ID de fluxo para funcionar corretamente.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-autocomplete'>;

export const AutomationV2ErrorCodes = {
  AutomationV2NotFound: {
    code: 'automation-v2/not-found',
    explanation: `A automação solicitada não existe.

**Causas comuns:**
- ID de automação incorreto
- Automação excluída
- Falta de permissão de acesso

**Como diagnosticar:**
Verifique se a automação aparece na lista de automações do fluxo.`,
    localizations: {},
    message: 'A automação não foi encontrada.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2'>;

export const AutomationV2ExecutionActionErrorCodes = {
  AutomationV2ExecutionActionAddCardAssigneesFailedToAddAssignee: {
    code: 'automation-v2-execution-action/add-card-assignees-failed-to-add-assignee',
    explanation: `Falha ao atribuir um usuário ao card.

**Causas comuns:**
- O usuário não tem permissão de acesso ao card ou pipe
- O usuário foi desativado
- O ID do usuário fornecido é inválido

**Como resolver:**
Verifique se o usuário alvo possui permissões adequadas no fluxo.`,
    localizations: {},
    message: 'A automação não conseguiu adicionar o responsável ao processo.',
    supportingDocs: [
      {
        name: 'Ação de Adicionar Responsável ao Card',
        path: '/wiki/docs/automations/actions/add-card-assignee',
      },
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  AutomationV2ExecutionActionAddCardAssigneesMissingCardId: {
    code: 'automation-v2-execution-action/add-card-assignees-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível adicionar o responsável ao card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Adicionar Responsável ao Card',
        path: '/wiki/docs/automations/actions/add-card-assignee',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionAddCardAssigneesNoAssigneesProvided: {
    code: 'automation-v2-execution-action/add-card-assignees-no-assignees-provided',
    explanation: `A lista de responsáveis a adicionar está vazia.

**Causas comuns:**
- Nenhum usuário foi selecionado na configuração
- A variável dinâmica de usuários retornou vazio

**Como resolver:**
Selecione pelo menos um usuário ou garanta que a variável dinâmica contenha IDs válidos.`,
    localizations: {},
    message: 'Nenhum responsável foi fornecido para adicionar ao card.',
    supportingDocs: [
      {
        name: 'Ação de Adicionar Responsável ao Card',
        path: '/wiki/docs/automations/actions/add-card-assignee',
      },
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  AutomationV2ExecutionActionAddCardCommentFailedToAddComment: {
    code: 'automation-v2-execution-action/add-card-comment-failed-to-add-comment',
    explanation: `Erro ao salvar o comentário no card.

**Causas comuns:**
- Conteúdo do comentário inválido ou muito longo
- Permissões insuficientes para comentar
- Card bloqueado ou arquivado (dependendo da configuração)

**Como resolver:**
Verifique se o texto do comentário está dentro dos limites permitidos.`,
    localizations: {},
    message: 'A automação não conseguiu adicionar o comentário ao processo.',
    supportingDocs: [
      {
        name: 'Ação de Adicionar Comentário ao Card',
        path: '/wiki/docs/automations/actions/add-card-comment',
      },
    ],
  },
  AutomationV2ExecutionActionAddCardCommentMissingCardId: {
    code: 'automation-v2-execution-action/add-card-comment-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível adicionar o comentário ao card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ações de Adicionar Comentário ao Card',
        path: '/wiki/docs/automations/actions/add-card-comment',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionAddCustomVariableEmptyKey: {
    code: 'automation-v2-execution-action/add-custom-variable-empty-key',
    explanation: `Chave da variável personalizada não fornecida.

**Causas comuns:**
- Campo de chave deixado em branco
- O resultado da interpolação é uma string vazia

**Como resolver:**
Forneça uma chave válida para a variável personalizada. Ou garanta que a interpolação resulte em um valor não vazio.`,
    localizations: {},
    message: 'A chave da variável personalizada não pode estar vazia.',
    supportingDocs: [
      {
        name: 'Ação de Adicionar Variável Personalizada',
        path: '/wiki/docs/automations/actions/add-custom-variables',
      },
    ],
  },
  AutomationV2ExecutionActionAddCustomVariableInvalidValue: {
    code: 'automation-v2-execution-action/add-custom-variable-invalid-value',
    explanation: `Valor da variável personalizada não suportado.

**Causas comuns:**
- Valor muito grande
- No caso de tipos complexos (objetos, arrays), o formato está incorreto ou o JSON é inválido
- O valor não pôde ser convertido para o tipo esperado e o comportamento não é de falha silenciosa

**Como resolver:**
Ajuste o valor para que esteja dentro dos limites aceitáveis e no formato correto para o tipo de dado esperado.
> Caso queira ignorar erros de conversão, altere a configuração definida em \`invalid_value_behavior\`.\n`,
    localizations: {},
    message: 'O valor da variável personalizada é inválido.',
    supportingDocs: [
      {
        name: 'Ação de Adicionar Variável Personalizada',
        path: '/wiki/docs/automations/actions/add-custom-variables',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardDeadlineFailedToCreateDeadline: {
    code: 'automation-v2-execution-action/create-card-deadline-failed-to-create-deadline',
    explanation: `Erro ao tentar criar o prazo.

**Causas comuns:**
- O card já possui um prazo definido para este escopo (etapa ou geral).
- O modelo de prazo não existe.

**Como resolver:**
Verifique se o card já possui o prazo que você está tentando criar.`,
    localizations: {},
    message: 'Não foi possível criar o prazo para o card.',
  },
  AutomationV2ExecutionActionCreateCardDeadlineMissingCardId: {
    code: 'automation-v2-execution-action/create-card-deadline-missing-card-id',
    explanation: `ID do card não fornecido.
    
**Causas comuns:**
- A variável \`context.card.id\` não foi resolvida.

**Como resolver:**
Certifique-se de que a automação tem um card no contexto ou forneça um ID manualmente.`,
    localizations: {},
    message: 'O ID do card é obrigatório para criar um prazo.',
  },
  AutomationV2ExecutionActionCreateCardDeadlineMissingDeadlineId: {
    code: 'automation-v2-execution-action/create-card-deadline-missing-deadline-id',
    explanation: `ID do modelo de prazo não fornecido.

**Como resolver:**
Selecione um modelo de prazo na configuração da ação.`,
    localizations: {},
    message: 'O ID do modelo de prazo é obrigatório.',
  },
  AutomationV2ExecutionActionCreateCardReviewFailedToCancelPendingReview: {
    code: 'automation-v2-execution-action/create-card-review-failed-to-cancel-pending-review',
    explanation: `Falha ao tentar substituir uma revisão de card existente.

**Causas comuns:**
- O sistema não conseguiu cancelar a revisão anterior devido a restrições de estado
- Concorrência de atualizações no mesmo card

**Como resolver:**
Verifique se é possível configurar a automação para aguardar ou ignorar se já houver revisão.`,
    localizations: {},
    message: 'Existe uma revisão pendente para este card e não é possível cancelar a revisão.',
    supportingDocs: [
      {
        name: 'Ação de Criar Revisão de Card',
        path: '/wiki/docs/automations/actions/create-card-review',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardReviewFailedToCreateReview: {
    code: 'automation-v2-execution-action/create-card-review-failed-to-create-review',
    explanation: `Erro genérico ao criar a revisão.

**Causas comuns:**
- Dados inválidos no template de revisão
- Configurações de fase de revisão inválidas

**Como diagnosticar:**
Revise as configurações da ação de criação de revisão.`,
    localizations: {},
    message: 'Houve um erro ao tentar criar a revisão do card.',
    supportingDocs: [
      {
        name: 'Ação de Criar Revisão de Card',
        path: '/wiki/docs/automations/actions/create-card-review',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardReviewMissingCardId: {
    code: 'automation-v2-execution-action/create-card-review-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível criar a revisão do card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Criar Revisão de Card',
        path: '/wiki/docs/automations/actions/create-card-review',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardReviewMissingTemplateId: {
    code: 'automation-v2-execution-action/create-card-review-missing-template-id',
    explanation: `O Template de Revisão não foi especificado.

**Causas comuns:**
- Template deletado
- Configuração da ação incompleta

**Como resolver:**
Selecione um template de revisão válido na configuração da ação.`,
    localizations: {},
    message:
      'Não foi possível criar a revisão do card porque o ID do template está faltando. Verifique se o template existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Criar Revisão de Card',
        path: '/wiki/docs/automations/actions/create-card-review',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardReviewPendingReviewAlreadyExists: {
    code: 'automation-v2-execution-action/create-card-review-pending-review-already-exists',
    explanation: `A ação foi abortada porque já existe uma revisão pendente, conforme configurado.

**Causas comuns:**
- Comportamento padrão de "Skip" (pular) quando duplicado
- Trigger disparado múltiplas vezes rápidas

**Como resolver:**
Isso geralmente é o comportamento esperado. Se deseja sobrescrever, altere a configuração da ação.`,
    localizations: {},
    message:
      'Já existe uma revisão pendente para este card e o comportamento definido é de não criar uma nova revisão.',
    supportingDocs: [
      {
        name: 'Ação de Criar Revisão de Card',
        path: '/wiki/docs/automations/actions/create-card-review',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardTaskFailedToCreateTask: {
    code: 'automation-v2-execution-action/create-card-task-failed-to-create-task',
    explanation: `Erro ao criar uma subtarefa (task) no card.

**Causas comuns:**
- Erro de validação nos campos da tarefa
- Template de tarefa inválido

**Como diagnosticar:**
Verifique se os dados obrigatórios da tarefa estão sendo preenchidos.`,
    localizations: {},
    message: 'A automação não conseguiu criar a tarefa no processo.',
    supportingDocs: [
      {
        name: 'Ação de Criar Tarefa de Card',
        path: '/wiki/docs/automations/actions/create-card-task',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardTaskMissingCardId: {
    code: 'automation-v2-execution-action/create-card-task-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível criar a tarefa do card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Criar Tarefa de Card',
        path: '/wiki/docs/automations/actions/create-card-task',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionCreateCardTaskMissingTemplateId: {
    code: 'automation-v2-execution-action/create-card-task-missing-template-id',
    explanation: `ID do template da tarefa não encontrado.

**Causas comuns:**
- Configuração incompleta da ação
- Template de tarefa removido

**Como resolver:**
Selecione um template de tarefa válido.`,
    localizations: {},
    message:
      'Não foi possível criar a tarefa do card porque o ID do template está faltando. Verifique se o template existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Criar Tarefa de Card',
        path: '/wiki/docs/automations/actions/create-card-task',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentDriveFileNotVisible: {
    code: 'automation-v2-execution-action/generate-document-drive-file-not-visible',
    explanation: `O arquivo modelo no Google Drive não tem as permissões corretas.

**Causas comuns:**
- O arquivo está privado
- A conta de serviço da automação não tem acesso ao arquivo

**Como resolver:**
Compartilhe o arquivo do Drive com permissão de leitura pública ou com o e-mail de serviço da integração.`,
    localizations: {},
    message: 'O arquivo do Google Drive não está configurado para acesso público.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToCreateAttachment: {
    code: 'automation-v2-execution-action/generate-document-failed-to-create-attachment',
    explanation: `O documento foi gerado, mas falhou ao ser anexado ao card.

**Causas comuns:**
- Falha no upload para o storage
- Card foi excluído durante o processo

**Como diagnosticar:**
Tente executar novamente a automação.`,
    localizations: {},
    message: 'Falha ao criar o anexo do documento no card.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToDownloadTemplate: {
    code: 'automation-v2-execution-action/generate-document-failed-to-download-template',
    explanation: `Não foi possível baixar o arquivo de modelo.

**Causas comuns:**
- Erro de conexão com Google Drive
- Arquivo excluído ou movido

**Como resolver:**
Verifique se o link do template ainda é válido.`,
    localizations: {},
    message: 'Falha ao baixar o template do Google Docs.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToGetPresignedUrl: {
    code: 'automation-v2-execution-action/generate-document-failed-to-get-presigned-url',
    explanation: `Erro interno ao gerar URL de upload seguro.

**Causas comuns:**
- Instabilidade no serviço de storage

**Como resolver:**
Tente novamente em alguns instantes.`,
    localizations: {},
    message: 'Falha ao obter a URL pré-assinada para o documento.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentFailedToUpload: {
    code: 'automation-v2-execution-action/generate-document-failed-to-upload',
    explanation: `Erro durante o upload do PDF gerado.

**Causas comuns:**
- Arquivo gerado muito grande
- Timeout de conexão

**Como resolver:**
Verifique se o template não gera um arquivo excessivamente grande.`,
    localizations: {},
    message: 'Falha ao fazer o upload do documento.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentInvalidDrivePath: {
    code: 'automation-v2-execution-action/generate-document-invalid-drive-path',
    explanation: `O link fornecido não parece ser de um arquivo Google Drive válido.

**Causas comuns:**
- Link de pasta em vez de arquivo
- Link de outro serviço (Dropbox, OneDrive)
- URL mal formatada

**Como resolver:**
Use o link direto de compartilhamento de um arquivo Google Docs/Slides/Sheets.`,
    localizations: {},
    message: 'A URL fornecida não é um link válido de um arquivo do Google Drive.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentInvalidUrl: {
    code: 'automation-v2-execution-action/generate-document-invalid-url',
    explanation: `A URL do template não tem um formato válido.

**Causas comuns:**
- URL incompleta
- Caracteres inválidos na URL

**Como resolver:**
Copie novamente o link do navegador.`,
    localizations: {},
    message: 'A URL fornecida é inválida.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentMissingCardId: {
    code: 'automation-v2-execution-action/generate-document-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível gerar o documento pois o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionGenerateDocumentUrlNotDrive: {
    code: 'automation-v2-execution-action/generate-document-url-not-drive',
    explanation: `O domínio da URL não pertence ao Google Drive.

**Causas comuns:**
- Uso de encurtadores de URL
- Links de visualizadores externos

**Como resolver:**
Utilize URLs oficias do Google Drive (docs.google.com, drive.google.com).`,
    localizations: {},
    message: 'A URL fornecida não é um link do Google Drive.',
    supportingDocs: [
      {
        name: 'Ação de Gerar Documento',
        path: '/wiki/docs/automations/actions/generate-document',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestExpectedStatusCodeMismatch: {
    code: 'automation-v2-execution-action/make-http-request-expected-status-code-mismatch',
    explanation: `A requisição foi completada, mas retornou um status HTTP diferente do configurado como sucesso.

**Causas comuns:**
- API externa retornou erro (4xx, 5xx)
- Expectativa configurada como 200 mas recebeu 201

**Como diagnosticar:**
Verifique os logs da execução para ver o status code real retornado pela API externa.`,
    localizations: {},
    message: 'O código de status retornado pela requisição HTTP não corresponde ao esperado.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlDirectIpNotAllowed: {
    code: 'automation-v2-execution-action/make-http-request-url-direct-ip-not-allowed',
    explanation: `Por segurança, requisições para IPs diretos são bloqueadas.

**Causas comuns:**
- Uso de \`http://1.2.3.4\` em vez de domínio
- URLs internas de rede

**Como resolver:**
Utilize nomes de domínio (DNS) qualificados.`,
    localizations: {},
    message: 'Não é permitido utilizar endereços IP diretamente na URL da requisição HTTP.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlInvalidOrUnresolvable: {
    code: 'automation-v2-execution-action/make-http-request-url-invalid-or-unresolvable',
    explanation: `O DNS não conseguiu resolver o domínio da URL.

**Causas comuns:**
- Domínio inexistente
- Erro de digitação na URL
- Falha temporária de DNS

**Como resolver:**
Verifique se a URL está correta e acessível publicamente.`,
    localizations: {},
    message: 'A URL informada na requisição HTTP é inválida ou não pode ser resolvida.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlPortNotAllowed: {
    code: 'automation-v2-execution-action/make-http-request-url-port-not-allowed',
    explanation: `A porta especificada na URL está bloqueada.

**Causas comuns:**
- Tentativa de usar portas não-padrão (diferentes de 80 e 443)
- Portas de serviços internos (ex: 22, 25, 3306)

**Como resolver:**
Utilize apenas portas HTTP (80) ou HTTPS (443).`,
    localizations: {},
    message: 'A porta informada na URL da requisição HTTP não é permitida.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlPrivateNetwork: {
    code: 'automation-v2-execution-action/make-http-request-url-private-network',
    explanation: `Tentativa de acesso a endereços de rede privada (localhost, 192.168.x.x, etc).

**Causas comuns:**
- URL aponta para servidor local de desenvolvimento
- DNS resolve para IP privado (DNS Rebinding protection)

**Como resolver:**
A automação só pode acessar URLs públicas na internet. Use um túnel (ex: ngrok) para testes locais.`,
    localizations: {},
    message: 'Não é permitido realizar requisições HTTP para redes privadas.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlProtocolNotAllowed: {
    code: 'automation-v2-execution-action/make-http-request-url-protocol-not-allowed',
    explanation: `Protocolo da URL não suportado.

**Causas comuns:**
- Uso de \`ftp://\`, \`file://\` ou outros protocolos
- Esquecer o protocolo (ex: começar direto com \`www\`)

**Como resolver:**
Use sempre \`http://\` ou \`https://\`.`,
    localizations: {},
    message: 'O protocolo informado na URL da requisição HTTP não é permitido.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlResolvedIpBlacklisted: {
    code: 'automation-v2-execution-action/make-http-request-url-resolved-ip-blacklisted',
    explanation: `O IP de destino está na lista de bloqueio de segurança.

**Causas comuns:**
- IP conhecido por malware/spam
- IPs de infraestrutura interna do provedor de cloud

**Como resolver:**
Verifique se o destino da requisição é seguro e público.`,
    localizations: {},
    message: 'O endereço IP resolvido da URL não é permitido.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMoveCardToStageFailedToMoveCard: {
    code: 'automation-v2-execution-action/move-card-to-stage-failed-to-move-card',
    explanation: `Falha ao tentar mover o card de fase.

**Causas comuns:**
- Violação de regras de movimentação (campos obrigatórios não preenchidos)
- Fase de destino inválida ou arquivada
- Usuário da automação sem permissão para mover card

**Como diagnosticar:**
Verifique se todas as regras da fase de destino são atendidas pelos dados atuais do card.`,
    localizations: {},
    message: 'A automação não conseguiu mover o processo para a etapa especificada.',
    supportingDocs: [
      {
        name: 'Ação de Mover Card para Etapa',
        path: '/wiki/docs/automations/actions/move-card-to-stage',
      },
      {
        name: 'Configuração de Etapas',
        path: '/wiki/docs/pipe/stages/configuration',
      },
    ],
  },
  AutomationV2ExecutionActionMoveCardToStageMissingCardId: {
    code: 'automation-v2-execution-action/move-card-to-stage-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível mover o card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Mover Card para Etapa',
        path: '/wiki/docs/automations/actions/move-card-to-stage',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionMoveCardToStageMissingStageId: {
    code: 'automation-v2-execution-action/move-card-to-stage-missing-stage-id',
    explanation: `ID da etapa de destino não configurado.

**Causas comuns:**
- Etapa não selecionada na configuração
- Etapa removida do fluxo

**Como resolver:**
Selecione uma etapa de destino válida.`,
    localizations: {},
    message:
      'Não foi possível mover o card porque o ID da etapa está faltando. Verifique se a etapa existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Mover Card para Etapa',
        path: '/wiki/docs/automations/actions/move-card-to-stage',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionRemoveCardAssigneesFailedToRemoveAssignee: {
    code: 'automation-v2-execution-action/remove-card-assignees-failed-to-remove-assignee',
    explanation: `Falha ao remover atribuição de usuário.

**Causas comuns:**
- Usuário não estava atribuído ao card
- Erro de permissão

**Como diagnosticar:**
Verifique se o usuário realmente estava atribuído no momento da execução.`,
    localizations: {},
    message: 'Houve um erro ao tentar remover o responsável do card.',
    supportingDocs: [
      {
        name: 'Ação de Remover Responsáveis do Card',
        path: '/wiki/docs/automations/actions/remove-card-assignee',
      },
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  AutomationV2ExecutionActionRemoveCardAssigneesMissingCardId: {
    code: 'automation-v2-execution-action/remove-card-assignees-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message:
      'Não foi possível remover o responsável do card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Remover Responsáveis do Card',
        path: '/wiki/docs/automations/actions/remove-card-assignee',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionRemoveCardAssigneesNoAssigneesProvided: {
    code: 'automation-v2-execution-action/remove-card-assignees-no-assignees-provided',
    explanation: `Lista de responsáveis vazia.

**Causas comuns:**
- Nenhuma seleção feita na configuração
- Variável dinâmica vazia

**Como resolver:**
Indique quais usuários devem ser removidos.`,
    localizations: {},
    message: 'Nenhum responsável foi fornecido para remover do card.',
    supportingDocs: [
      {
        name: 'Ação de Remover Responsáveis do Card',
        path: '/wiki/docs/automations/actions/remove-card-assignee',
      },
    ],
  },
  AutomationV2ExecutionActionRequestSolicitationChangesFailed: {
    code: 'automation-v2-execution-action/request-solicitation-changes-failed',
    explanation: `Falha ao tentar devolver o card ao solicitante original.

**Causas comuns:**
- O card não é uma solicitação válida
- O solicitante original não foi encontrado ou está inativo
- Problemas técnicos ao atualizar o responsável pelo formulário inicial

**Como resolver:**
Verifique se o card é uma solicitação e se o usuário que a criou ainda possui acesso ao sistema.`,
    localizations: {},
    message: 'A automação não conseguiu solicitar as alterações do formulário.',
    supportingDocs: [
      {
        name: 'Ação de Solicitar Alterações da Solicitação',
        path: '/wiki/docs/automations/actions/request-solicitation-changes',
      },
    ],
  },
  AutomationV2ExecutionActionRequestSolicitationChangesMissingCardId: {
    code: 'automation-v2-execution-action/request-solicitation-changes-missing-card-id',
    explanation: `ID do card é obrigatório para esta ação.

**Causas comuns:**
- Variável cardId não resolvida ou nula no contexto da automação.

**Como resolver:**
Certifique-se de que o ID do card está sendo passado corretamente através do contexto ou mapeamento manual.`,
    localizations: {},
    message: 'Não foi possível solicitar alterações porque o ID do card está faltando.',
    supportingDocs: [
      {
        name: 'Ação de Solicitar Alterações da Solicitação',
        path: '/wiki/docs/automations/actions/request-solicitation-changes',
      },
    ],
  },
  AutomationV2ExecutionActionResubmitForApprovalFailed: {
    code: 'automation-v2-execution-action/resubmit-for-approval-failed',
    explanation: `Falha ao tentar retornar a aprovação para o status pendente.

**Causas comuns:**
- A aprovação não possui status de "alterações solicitadas"
- A revisão já foi concluída ou cancelada
- Problemas técnicos ao atualizar o status

**Como resolver:**
Verifique se a aprovação está de fato com status de "alterações solicitadas".`,
    localizations: {},
    message: 'A automação não conseguiu reenviar a aprovação.',
    supportingDocs: [
      {
        name: 'Ação de Reenviar para Aprovação',
        path: '/wiki/docs/automations/actions/resubmit-solicitation',
      },
    ],
  },
  AutomationV2ExecutionActionResubmitForApprovalMissingCardId: {
    code: 'automation-v2-execution-action/resubmit-for-approval-missing-card-id',
    explanation: `ID do card é obrigatório para esta ação.

**Causas comuns:**
- Variável cardId não resolvida ou nula no contexto da automação.

**Como resolver:**
Certifique-se de que o ID do card está sendo passado corretamente através do contexto ou mapeamento manual.`,
    localizations: {},
    message: 'Não foi possível reenviar para aprovação porque o ID do card está faltando.',
    supportingDocs: [
      {
        name: 'Ação de Reenviar para Aprovação',
        path: '/wiki/docs/automations/actions/resubmit-solicitation',
      },
    ],
  },
  AutomationV2ExecutionActionResubmitForApprovalNoLastReview: {
    code: 'automation-v2-execution-action/resubmit-for-approval-no-last-review',
    explanation: `O card precisa ter uma aprovação em andamento para poder reenviá-la.

**Causas comuns:**
- O card nunca teve uma solicitação de aprovação
- A aprovação já foi concluída ou cancelada

**Como resolver:**
Verifique se o card possui uma aprovação com status de "alterações solicitadas".`,
    localizations: {},
    message:
      'Não foi possível reenviar para aprovação porque o card não possui uma aprovação ativa.',
    supportingDocs: [
      {
        name: 'Ação de Reenviar para Aprovação',
        path: '/wiki/docs/automations/actions/resubmit-solicitation',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailFailedToSendEmail: {
    code: 'automation-v2-execution-action/send-email-failed-to-send-email',
    explanation: `Erro no serviço de envio de e-mails.

**Causas comuns:**
- Problema no provedor de e-mail (SMTP)
- Rate limit excedido

**Como diagnosticar:**
Tente reexecutar a automação.`,
    localizations: {},
    message: 'A automação não conseguiu enviar o e-mail.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailInvalidDomain: {
    code: 'automation-v2-execution-action/send-email-invalid-domain',
    explanation: `Domínio do remetente ou destinatário inválido ou bloqueado.

**Causas comuns:**
- Domínio sem registros MX válidos
- Domínio na blacklist de spam

**Como resolver:**
Use domínios de e-mail válidos e confiáveis.`,
    localizations: {},
    message: 'O domínio do e-mail é inválido.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailInvalidEmailFormat: {
    code: 'automation-v2-execution-action/send-email-invalid-email-format',
    explanation: `Endereço de e-mail mal formatado detectado.

**Causas comuns:**
- Falta de \`@\` ou domínio
- Caracteres inválidos no endereço

**Como resolver:**
Verifique o formato dos endereços de e-mail (ex: \`nome@dominio.com\`).`,
    localizations: {},
    message: 'O formato do e-mail é inválido.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailMissingBody: {
    code: 'automation-v2-execution-action/send-email-missing-body',
    explanation: `Conteúdo da mensagem vazio.

**Causas comuns:**
- Campo de corpo do e-mail não preenchido
- Variável de template vazia

**Como resolver:**
Escreva o conteúdo da mensagem na configuração da ação.`,
    localizations: {},
    message:
      'O corpo do e-mail está faltando. Verifique se o corpo existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailMissingRecipients: {
    code: 'automation-v2-execution-action/send-email-missing-recipients',
    explanation: `Nenhum destinatário definido para o e-mail.

**Causas comuns:**
- Campo "Para" vazio
- Variável dinâmica de e-mail vazia

**Como resolver:**
Defina pelo menos um destinatário válido na ação.`,
    localizations: {},
    message: 'Os destinatários do e-mail estão faltando.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailMissingSubject: {
    code: 'automation-v2-execution-action/send-email-missing-subject',
    explanation: `Assunto do e-mail vazio.

**Causas comuns:**
- Campo "Assunto" não preenchido

**Como resolver:**
Adicione um assunto ao e-mail para evitar que caia em spam.`,
    localizations: {},
    message: 'O assunto do e-mail está faltando.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailNoValidRecipientsProvided: {
    code: 'automation-v2-execution-action/send-email-no-valid-recipients-provided',
    explanation: `Todos os destinatários fornecidos foram considerados inválidos.

**Causas comuns:**
- Lista de e-mails com erros de digitação
- E-mails bloqueados pelo sistema

**Como diagnosticar:**
Revise a lista de e-mails destinatários.`,
    localizations: {},
    message: 'Nenhum destinatário válido foi fornecido.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
    ],
  },
  AutomationV2ExecutionActionSendEmailUndeliverableEmail: {
    code: 'automation-v2-execution-action/send-email-undeliverable-email',
    explanation: `Bounce ou rejeição imediata do servidor de e-mail.

**Causas comuns:**
- Caixa de entrada do destinatário cheia
- Domínio de destino rejeitou a conexão

**Como resolver:**
Verifique se o e-mail do destinatário está correto e ativo.`,
    localizations: {},
    message: 'O endereço de e-mail especificado não pôde ser entregue.',
    supportingDocs: [
      {
        name: 'Ação de Enviar E-mail',
        path: '/wiki/docs/automations/actions/send-email',
      },
    ],
  },
  AutomationV2ExecutionActionToggleCardArchivedFailedToToggleCardArchived: {
    code: 'automation-v2-execution-action/toggle-card-archived-failed-to-toggle-card-archived',
    explanation: `Falha ao arquivar/desarquivar card.

**Causas comuns:**
- Card já está no estado desejado (se a ação for explícita)
- Erro de permissão ou bloqueio do card

**Como diagnosticar:**
Verifique o estado atual do card.`,
    localizations: {},
    message: 'Houve um erro ao tentar alternar o estado arquivado do card.',
    supportingDocs: [
      {
        name: 'Ação de Arquivar/Desarquivar Card',
        path: '/wiki/docs/automations/actions/toggle-card-archived',
      },
    ],
  },
  AutomationV2ExecutionActionToggleCardArchivedMissingCardId: {
    code: 'automation-v2-execution-action/toggle-card-archived-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message: 'ID do card não foi fornecido.',
    supportingDocs: [
      {
        name: 'Ação de Arquivar/Desarquivar Card',
        path: '/wiki/docs/automations/actions/toggle-card-archived',
      },
    ],
  },
  AutomationV2ExecutionActionToggleCardLockedCastToBooleanFailed: {
    code: 'automation-v2-execution-action/toggle-card-locked-cast-to-boolean-failed',
    explanation: `Valor inválido para o estado de bloqueio (esperado booleano).

**Causas comuns:**
- Passar string "sim"/"não" em vez de true/false

**Como resolver:**
Use valores booleanos (true/false) para configurar o bloqueio.`,
    localizations: {},
    message: 'Houve um erro ao tentar converter o estado de bloqueio do card.',
    supportingDocs: [
      {
        name: 'Ação de Bloquear/Desbloquear Card',
        path: '/wiki/docs/automations/actions/toggle-card-lock',
      },
    ],
  },
  AutomationV2ExecutionActionToggleCardLockedFailedToToggleCardLocked: {
    code: 'automation-v2-execution-action/toggle-card-locked-failed-to-toggle-card-locked',
    explanation: `Falha ao bloquear/desbloquear card.

**Causas comuns:**
- Erro de permissão
- Card em estado inconsistente

**Como diagnosticar:**
Verifique se o usuário da automação tem permissão para bloquear cards.`,
    localizations: {},
    message: 'Houve um erro ao tentar alternar o estado de bloqueio do card.',
    supportingDocs: [
      {
        name: 'Ação de Bloquear/Desbloquear Card',
        path: '/wiki/docs/automations/actions/toggle-card-lock',
      },
    ],
  },
  AutomationV2ExecutionActionToggleCardLockedMissingCardId: {
    code: 'automation-v2-execution-action/toggle-card-locked-missing-card-id',
    explanation: `ID do card é obrigatório no contexto da ação. O id do card é automaticamente passado pelo gatilho ou por ações anteriores.

**Causas comuns:**
- Variável cardId não resolvida
- Caso o valor do cardId seja mapeado manualmente, pode ter sido digitado incorretamente.
- Caso o valor do cardId não tenha sido mapeado, o \`context.card.id\` não está disponível.

**Como resolver:**
Corrija o mapeamento do ID do card.`,
    localizations: {},
    message: 'ID do card não foi fornecido.',
    supportingDocs: [
      {
        name: 'Ação de Bloquear/Desbloquear Card',
        path: '/wiki/docs/automations/actions/toggle-card-lock',
      },
    ],
  },
  AutomationV2ExecutionActionUpdateCardTaskStatusFailedToUpdateTaskStatus: {
    code: 'automation-v2-execution-action/update-card-task-status-failed-to-update-task-status',
    explanation: `Falha ao marcar tarefa como feita/não feita.

**Causas comuns:**
- Tarefa não existe mais
- Tarefa em card arquivado

**Como diagnosticar:**
Verifique se a tarefa e o card ainda existem e estão ativos.`,
    localizations: {},
    message: 'Houve um erro ao tentar atualizar o status da tarefa do card.',
    supportingDocs: [
      {
        name: 'Ação de Atualizar Status da Tarefa do Card',
        path: '/wiki/docs/automations/actions/update-card-task-status',
      },
      {
        name: 'Alterando o Status das Tarefas',
        path: '/wiki/docs/cards/tasks/updating-task-status',
      },
    ],
  },
  AutomationV2ExecutionActionUpdateCardTaskStatusMissingCardTaskId: {
    code: 'automation-v2-execution-action/update-card-task-status-missing-card-task-id',
    explanation: `ID da tarefa é obrigatório.

**Causas comuns:**
- Variável taskId não resolvida

**Como resolver:**
Forneça o ID da task a ser atualizada.`,
    localizations: {},
    message: 'ID da tarefa do card não foi fornecido.',
    supportingDocs: [
      {
        name: 'Ação de Atualizar Status da Tarefa do Card',
        path: '/wiki/docs/automations/actions/update-card-task-status',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action'>;

export const AutomationV2ExecutionActionGenericErrorCodes = {
  AutomationV2ExecutionActionGenericAttemptToCastValueFailed: {
    code: 'automation-v2-execution-action-generic/attempt-to-cast-value-failed',
    explanation: `Falha de conversão de tipos (Type Casting).

**Causas comuns:**
- Tentar usar texto em campos numéricos
- Formato de data inválido

**Como resolver:**
Garanta que os dados passados para a ação correspondam aos tipos esperados (Número, Texto, Data, Booleano).`,
    localizations: {},
    message:
      'Não foi possível converter um valor da automação para o tipo de dados esperado. Verifique os dados.',
    supportingDocs: [
      {
        name: 'Conversão de Valores',
        path: '/wiki/docs/automations/convercao-de-valores',
      },
      {
        name: 'Conversão de Valores nas Condições',
        path: '/wiki/docs/automations/condicoes#conversão-de-valores',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action-generic'>;

export const AutomationV2ExecutionErrorCodes = {
  AutomationV2ExecutionAutomationIdRequired: {
    code: 'automation-v2-execution/automation-id-required',
    explanation: `ID da automação não encontrado no contexto de execução.

**Causas comuns:**
- Erro interno de contexto
- Chamada direta de execução sem passar metadados

**Como diagnosticar:**
Isso geralmente é um erro interno ou de integração via API.`,
    localizations: {},
    message: 'O ID da automação é obrigatório para executar esta ação.',
  },
  AutomationV2ExecutionInvalidOperation: {
    code: 'automation-v2-execution/invalid-operation',
    explanation: `A operação tentada não é permitida no estado atual.

**Causas comuns:**
- Ação incompatível com o tipo de gatilho
- Tentativa de modificar dados somente leitura

**Como resolver:**
Revise a lógica da automação.`,
    localizations: {},
    message: 'Operação inválida durante a execução da automação.',
  },
  AutomationV2ExecutionNotFound: {
    code: 'automation-v2-execution/not-found',
    explanation: `O registro da execução não foi encontrado.

**Causas comuns:**
- ID de execução inválido
- Logs de execução expirados

**Como diagnosticar:**
Verifique se o ID da execução está correto.`,
    localizations: {},
    message: 'Execução da automação não encontrada.',
  },
  AutomationV2ExecutionRecursiveLimitReached: {
    code: 'automation-v2-execution/recursive-limit-reached',
    explanation: `A proteção contra loops infinitos foi ativada.

**Causas comuns:**
- Automação A dispara Automação B que dispara Automação A
- Trigger "Quando card for movido" movendo o card para a mesma fase

**Como resolver:**
Revise a lógica para evitar ciclos de disparo contínuo.`,
    localizations: {},
    message: 'A automação atingiu o limite de execuções encadeadas (loop infinito detectado).',
    supportingDocs: [
      {
        name: 'Limites das Automações',
        path: '/wiki/docs/automations/limites-das-automacoes',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution'>;

export const AutomationV2RoleErrorCodes = {
  AutomationV2RoleAlreadyExists: {
    code: 'automation-v2-role/already-exists',
    explanation: `Conflito de papéis (Roles).

**Causas comuns:**
- Tentativa de adicionar um papel duplicado

**Como resolver:**
Use o papel já existente.`,
    localizations: {},
    message: 'Já existe um papel designado para esta automação nesse fluxo.',
  },
  AutomationV2RoleForbiddenInDatabase: {
    code: 'automation-v2-role/forbidden-in-database',
    explanation: `Papel inválido para contexto de Banco de Dados.

**Causas comuns:**
- Papel exclusivo de pipes usado em banco de dados

**Como resolver:**
Escolha um papel compatível com bancos de dados.`,
    localizations: {},
    message: 'Este cargo de banco de dados não pode ser atribuído a automações.',
    supportingDocs: [
      {
        name: 'Permissões de Automação',
        path: '/wiki/docs/automations/seguranca-e-permissoes',
      },
      {
        name: 'Cargos e Permissões',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  AutomationV2RoleForbiddenInGlobal: {
    code: 'automation-v2-role/forbidden-in-global',
    explanation: `Papel inválido para contexto Global.

**Causas comuns:**
- Restrição de segurança para automações globais

**Como resolver:**
Use um papel permitido para automações globais.`,
    localizations: {},
    message: 'Este cargo global não pode ser atribuído a automações.',
    supportingDocs: [
      {
        name: 'Permissões de Automação',
        path: '/wiki/docs/automations/seguranca-e-permissoes',
      },
      {
        name: 'Cargos e Permissões',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  AutomationV2RoleForbiddenInPipe: {
    code: 'automation-v2-role/forbidden-in-pipe',
    explanation: `Papel inválido para contexto de Pipe.

**Causas comuns:**
- Papel exclusivo de banco de dados usado em pipe

**Como resolver:**
Escolha um papel compatível com fluxos (pipes).`,
    localizations: {},
    message: 'Este cargo de fluxo não pode ser atribuído a automações.',
    supportingDocs: [
      {
        name: 'Permissões de Automação',
        path: '/wiki/docs/automations/seguranca-e-permissoes',
      },
      {
        name: 'Cargos e Permissões',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  AutomationV2RoleNotFound: {
    code: 'automation-v2-role/not-found',
    explanation: `Role não existe.

**Causas comuns:**
- Papel excluído
- ID incorreto

**Como diagnosticar:**
Verifique a lista de papéis disponíveis.`,
    localizations: {},
    message: 'Papel da automação não encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-role'>;

export const AutomationV2TriggerConditionErrorCodes = {
  AutomationV2TriggerConditionBothValueAndValues: {
    code: 'automation-v2-trigger-condition/both-value-and-values',
    explanation: `Configuração de condição ambígua.

**Causas comuns:**
- Definir \`value\` (singular) e \`values\` (plural) simultaneamente no JSON

**Como resolver:**
Use apenas um dos campos, dependendo do operador (ex: "igual" usa value, "está contido em" usa values).`,
    localizations: {},
    message: 'Não é permitido informar "valor" e "valores" ao mesmo tempo na condição do gatilho.',
    supportingDocs: [
      {
        name: 'Criando sua Automação',
        path: '/wiki/docs/automations/criando-sua-automacao',
      },
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionExpectedStringOnOperator: {
    code: 'automation-v2-trigger-condition/expected-string-on-operator',
    explanation: `Tipo de dado incorreto para o operador.

**Causas comuns:**
- Usar número ou booleano em operadores de texto (ex: "começa com")

**Como resolver:**
Forneça um valor de texto (string).`,
    localizations: {},
    message: 'O operador da condição do gatilho espera um valor do tipo texto.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionNoValueExpectedForOperator: {
    code: 'automation-v2-trigger-condition/no-value-expected-for-operator',
    explanation: `Valor fornecido onde não era esperado.

**Causas comuns:**
- Passar valor para operadores unários como "está vazio" ou "é nulo"

**Como resolver:**
Remova o campo de valor da configuração da condição.`,
    localizations: {},
    message: 'O operador da condição do gatilho não espera nenhum valor.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionNoValueOrValuesProvided: {
    code: 'automation-v2-trigger-condition/no-value-or-values-provided',
    explanation: `Valor ausente para a condição.

**Causas comuns:**
- Campos \`value\` ou \`values\` não preenchidos para operadores que exigem comparação (ex: "igual a")

**Como resolver:**
Forneça o valor a ser comparado.`,
    localizations: {},
    message: 'Nenhum valor foi informado para a condição do gatilho.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionNotFound: {
    code: 'automation-v2-trigger-condition/not-found',
    explanation: `Condição inexistente.

**Causas comuns:**
- ID de condição inválido

**Como diagnosticar:**
Verifique a estrutura do gatilho.`,
    localizations: {},
    message: 'Condição do gatilho não encontrada.',
  },
  AutomationV2TriggerConditionNotFoundForTriggerType: {
    code: 'automation-v2-trigger-condition/not-found-for-trigger-type',
    explanation: `Condição incompatível com o Trigger.

**Causas comuns:**
- Tentar usar condição de campo de data em gatilho que não tem contexto de data

**Como resolver:**
Use apenas condições suportadas pelo tipo de gatilho selecionado.`,
    localizations: {},
    message: 'A condição do gatilho não foi encontrada para o tipo de gatilho especificado.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionOperatorNotAllowed: {
    code: 'automation-v2-trigger-condition/operator-not-allowed',
    explanation: `Operador inválido.

**Causas comuns:**
- Usar "maior que" em campos de texto

**Como resolver:**
Selecione um operador compatível com o tipo de dado do campo.`,
    localizations: {},
    message: 'O operador especificado não é permitido para este tipo de condição.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionValueNotAllowed: {
    code: 'automation-v2-trigger-condition/value-not-allowed',
    explanation: `Valor proibido.

**Causas comuns:**
- Valores fora do range permitido
- Caracteres inválidos

**Como resolver:**
Verifique as regras de validação da condição.`,
    localizations: {},
    message: 'O valor especificado não é permitido para este tipo de condição.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-trigger-condition'>;

export const AutomationV2TriggerErrorCodes = {
  AutomationV2TriggerInvalidProperties: {
    code: 'automation-v2-trigger/invalid-properties',
    explanation: `Configuração de gatilho inválida.

**Causas comuns:**
- Campos obrigatórios do gatilho faltando
- Configuração de evento inconsistente

**Como diagnosticar:**
Revise a configuração do gatilho.`,
    localizations: {},
    message: 'As propriedades do gatilho da automação são inválidas.',
    supportingDocs: [
      {
        name: 'Gatilhos da Automação',
        path: '/wiki/docs/category/gatilhos',
      },
    ],
  },
  AutomationV2TriggerNotFound: {
    code: 'automation-v2-trigger/not-found',
    explanation: `Gatilho não encontrado.

**Causas comuns:**
- Gatilho removido
- Erro de ID

**Como resolver:**
Recrie o gatilho se necessário.`,
    localizations: {},
    message: 'O gatilho da automação não foi encontrado.',
  },
  AutomationV2TriggerNotFoundInVersion: {
    code: 'automation-v2-trigger/not-found-in-version',
    explanation: `Gatilho não existe nesta versão.

**Causas comuns:**
- Inconsistência de versionamento

**Como diagnosticar:**
Verifique a versão da automação.`,
    localizations: {},
    message: 'O gatilho especificado não foi encontrado nesta versão da automação.',
  },
  AutomationV2TriggerTypeNotFound: {
    code: 'automation-v2-trigger/type-not-found',
    explanation: `Tipo de gatilho desconhecido.

**Causas comuns:**
- Tipo descontinuado
- Feature flag desativada

**Como resolver:**
Escolha um tipo de gatilho disponível.`,
    localizations: {},
    message: 'O tipo de gatilho especificado não foi encontrado ou não está disponível.',
    supportingDocs: [
      {
        name: 'Gatilhos da Automação',
        path: '/wiki/docs/category/gatilhos',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-trigger'>;

export const AutomationV2VersionErrorCodes = {
  AutomationV2VersionMalformed: {
    code: 'automation-v2-version/malformed',
    explanation: `Dados da versão corrompidos.

**Causas comuns:**
- Erro de salvamento
- JSON inválido na estrutura da versão

**Como resolver:**
Tente salvar uma nova versão da automação.`,
    localizations: {},
    message: 'A versão da automação está malformada. Verifique os dados e tente novamente.',
  },
  AutomationV2VersionNotFound: {
    code: 'automation-v2-version/not-found',
    explanation: `Versão não encontrada.

**Causas comuns:**
- ID de versão incorreto
- Versão excluída

**Como diagnosticar:**
Verifique o histórico de versões.`,
    localizations: {},
    message: 'Versão da automação não encontrada.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-version'>;
